<i18n
  src="@/locales/views/organization/partners/producers/new_crop_field.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    name="org_partners_producers_show_producer_path"
    :params="{
      organization_id: organization_id,
      producer_id: producer_id
    }"
  />
  <bread-crumbs
    :labels="[module_name, producer?.organization_producer?.producer_code]"
  ></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <template v-if="load_general_data">
    <producer-info :producer="producer" :organization_id="organization_id" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-6">
                <div class="mt-1">
                  <switch-form
                    v-model="from_original"
                    :label="$t('model.crop_field.from_original')"
                  />
                </div>
              </div>
              <div v-if="from_original" class="sm:col-span-3">
                <label class="label-form">
                  {{ $t('model.crop_field.original_producer') }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="producers"
                    v-model="original_producer_id"
                  />
                </div>
              </div>
              <div v-if="from_original" class="sm:col-span-3">
                <label class="label-form">
                  {{ $t('model.crop_field.original_crop_field') }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_fields"
                    v-model="original_crop_field_id"
                    label="name"
                  />
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-4">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{
                    $t(
                      "model.crop_field.organization_crop_field.crop_field_code"
                    )
                  }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="organization_crop_field.crop_field_code"
                    :class="[
                      'input-text',
                      isInvalid('organization_crop_field.crop_field_code')
                    ]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.name") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="name"
                    :class="['input-text', isInvalid('name')]"
                  />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.hectares") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="hectares"
                    :class="['input-text', isInvalid('hectares')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.square_meters") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="square_meters"
                    :class="['input-text', isInvalid('square_meters')]"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.crop_field_type_id") }}
                </label>
                <div class="mt-1">
                  <radio-form
                    v-model="crop_field_type_id"
                    name="crop_field_type_id"
                    :options="cropFieldTypes"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <div class="mt-1">
                  <switch-form
                    v-model="organization_crop_field.active"
                    :label="$t('model.crop_field.organization_crop_field.active')"
                  />
                </div>
              </div>
            </div>
            <div class="col-span-6 sm:col-span-3">
              <label
                for="organization_crop_field_observations"
                class="block text-sm font-medium text-gray-700"
              >
                {{ $t("model.crop_field.organization_crop_field.observations") }}
              </label>
              <div class="mt-1">
                <textarea
                  v-model="organization_crop_field.observations"
                  id="organization_crop_field_observations"
                  name="organization_crop_field_observations"
                  rows="3"
                  class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 mt-1 block w-full sm:text-sm border border-gray-300 rounded-md"
                />
              </div>
              <em class="mt-1 text-sm text-gray-500">
                {{ $t("model.crop_field.organization_crop_field.observations_hint") }}
              </em>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" v-if="crop_field_type_id == 1">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.planting_date") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="planting_date" mode="month" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.harvest_time") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="harvest_time"
                    :class="['input-text', isInvalid('harvest_time')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.crop_id") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crops"
                    v-model="crop_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.crop_variety_id") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_varieties"
                    v-model="crop_variety_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop_variety')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.tree_number") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="tree_number"
                    :class="['input-text', isInvalid('tree_number')]"
                  />
                </div>
              </div>

            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.organization_crop_field.crop_field_location") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_field_locations"
                    :clearable="false"
                    v-model="crop_field_location_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop_field_location')"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.organization_crop_field.crop_field_zone") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_field_zones"
                    :clearable="false"
                    v-model="crop_field_zone_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop_field_zone')"
                  />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-6">
                <label class="label-form">
                  Tipo de coordenadas
                </label>
                <div class="mt-1">
                  <select-form
                    :options="coordinate_types"
                    :clearable="false"
                    v-model="coordinate_type"
                    label="name"
                  />
                </div>
              </div>

              <template v-if="coordinate_type === 1">
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.crop_field.longitude_dd") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="longitude_dd"
                      :class="['input-text', isInvalid('longitude_dd')]"
                    />
                    <em class="text-sm">Ejemplo: -80.701825</em>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.crop_field.latitude_dd") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="latitude_dd"
                      :class="['input-text', isInvalid('latitude_dd')]"
                    />
                    <em class="text-sm">Ejemplo: -5.192999</em>
                  </div>
                </div>
              </template>

              <template v-if="coordinate_type === 2">
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.crop_field.longitude_dms") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="longitude_dms"
                      :class="['input-text', isInvalid('longitude_dms')]"
                    />
                    <em class="text-sm">Ejemplo: 80º 42' 6.57'' O</em>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.crop_field.latitude_dms") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="latitude_dms"
                      :class="['input-text', isInvalid('latitude_dms')]"
                    />
                    <em class="text-sm">Ejemplo: 5º 11' 34.8'' S</em>
                  </div>
                </div>
              </template>

              <template v-if="coordinate_type === 3">
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.crop_field.zone_utm") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="zone_utm"
                      :class="['input-text', isInvalid('zone_utm')]"
                    />
                    <em class="text-sm">Ejemplo: 17</em>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.crop_field.longitude_utm") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="longitude_utm"
                      :class="['input-text', isInvalid('longitude_utm')]"
                    />
                    <em class="text-sm">Ejemplo: 500000</em>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.crop_field.latitude_utm") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="latitude_utm"
                      :class="['input-text', isInvalid('latitude_utm')]"
                    />
                    <em class="text-sm">Ejemplo: 9000000</em>
                  </div>
                </div>
              </template>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.altitude") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="altitude"
                    :class="['input-text', isInvalid('altitude')]"
                  />
                </div>
              </div>

            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowProducer()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="create()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>

  <loading-message v-else />
</template>

<script>

import CropFieldTypesMixin from "@/components/views/mixins/models/crop_field_types.js";

import Requester from "@/views/organization/requesters/partners/producers/manage_crop_field.js";
import RequesterManageProducer from "@/views/organization/requesters/partners/producers/manage_producer.js";
import ProducerInfo from "@/components/views/organization/partners/producers/ProducerInfo.vue";

export default {
  name: "OrgPartnersProducersNewCropField",
  mixins: [CropFieldTypesMixin],
  components: { ProducerInfo },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      producer: {},
      crops: [],
      crop_field_locations: [],
      crop_varieties: [],
      coordinate_type: null,
      coordinate_types: [
        { id: 1, name: 'Coordenadas decimales (Ej: -80.701825, -5.192999)' },
        { id: 2, name: 'Coordenadas sexagesimales (Ej: 80º 1\' 1\'\' O, 5º 1\' 1\'\' S)' },
        { id: 3, name: 'Coordenadas UTM (Ej: 17, 500000, 9000000)' }
      ],
      organization_crop_field: { crop_field_code: null, active: true, observations: null },
      name: null,
      hectares: null,
      square_meters: null,
      longitude_dd: null,
      latitude_dd: null,
      longitude_dms: null,
      latitude_dms: null,
      latitude_utm: null,
      longitude_utm: null,
      zone_utm: null,
      altitude: null,
      location: null,
      planting_date: null,
      harvest_time: null,
      tree_number: null,
      crop_id: null,
      crop_field_location_id: null,
      crop_field_zone_id: null,
      crop_variety_id: null,
      crop_field_type_id: 1,
      original_crop_field_id: null,
      from_original: false,
      producers: [],
      original_producer_id: null,
      crop_fields: [],
    };
  },
  props: {
    organization_id: {},
    producer_id: {}
  },
  computed: {
    crop_field_zones: function () {
      if (this.crop_field_location_id) {
        let location = this.crop_field_locations.find(location => location.id == this.crop_field_location_id);
        return location?.crop_field_zones || [];
      }
      return [];
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchProducer();
      await this.fetchCrops();
      await this.fetchCropFieldLocations();
      this.load_general_data = true;
    },
    fetchProducer: async function () {
      let response = await RequesterManageProducer.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    fetchCrops: async function () {
      let response = await Requester.fetchCrops(this.organization_id);

      this.crops = response.data;
    },
    fetchCropFieldLocations: async function () {
      let response = await Requester.fetchCropFieldLocations(this.organization_id);

      this.crop_field_locations = response.data;
    },
    fetchProducers: async function () {
      if (this.producers.length > 0) return

      let response = await Requester.fetchProducers(this.organization_id);
      this.producers = response.data;
    },
    fetchCropFields: async function () {
      let response = await Requester.fetchCropFields(this.organization_id, this.original_producer_id);

      this.crop_fields = response.data;
      if (this.crop_fields.length === 1) {
        this.original_crop_field_id = this.crop_fields[0].id;
      }
    },
    create: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.createCropField(
          this.organization_id,
          this.producer_id,
          {
            name: this.name,
            hectares: this.hectares,
            square_meters: this.square_meters,
            longitude_dd: this.longitude_dd,
            latitude_dd: this.latitude_dd,
            longitude_dms: this.longitude_dms,
            latitude_dms: this.latitude_dms,
            longitude_utm: this.longitude_utm,
            latitude_utm: this.latitude_utm,
            zone_utm: this.zone_utm,
            altitude: this.altitude,
            planting_date: this.planting_date,
            harvest_time: this.harvest_time,
            tree_number: this.tree_number,
            crop_id: this.crop_id,
            crop_variety_id: this.crop_variety_id,
            crop_field_type_id: this.crop_field_type_id,
            original_crop_field_id: this.original_crop_field_id,
            organization_crop_field: {
              crop_field_zone_id: this.crop_field_zone_id,
              crop_field_code: this.organization_crop_field.crop_field_code,
              active: this.organization_crop_field.active,
              observations: this.organization_crop_field.observations
            }
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowProducer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
        this.proc_save_button = false;
      }
    },
    goToShowProducer: function () {
      this.$router.push({
        name: "org_partners_producers_show_producer_path",
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id
        }
      });
    }
  },
  watch: {
    crop_id: function () {
      this.crop_variety_id = null;
      this.crop_varieties =
        this.crops.find(crop => crop.id == this.crop_id)?.crop_varieties || [];
    },
    crop_field_locations: function (crop_field_locations) {
      if (crop_field_locations.length == 1) {
        this.crop_field_location_id = crop_field_locations[0].id;
      }
    },
    crop_field_zones: function (crop_field_zones) {
      if (crop_field_zones.lenght == 1) {
        this.crop_field_zone_id = crop_field_zones[0].id;
      }
    },
    crop_field_location_id: function () {
      this.crop_field_zone_id = null;
    },
    from_original: function () {
      if (this.from_original) {
        this.fetchProducers();
      } else {
        this.original_producer_id = null;
        this.original_crop_field_id = null;
      }
    },
    original_producer_id: function () {
      this.original_crop_field_id = null;
      if (this.original_producer_id) {
        this.fetchCropFields();
      } else {
        this.crop_fields = [];
      }
    },
    original_crop_field_id: function () {
      if (this.original_crop_field_id) {
        const crop_field = this.crop_fields.find(
          crop_field => crop_field.id == this.original_crop_field_id
        );
        this.crop_id = crop_field.crop_id;
        this.planting_date = crop_field.planting_date;
        this.harvest_time = crop_field.harvest_time;
        const location = this.crop_field_locations.find((crop_field_location) =>
          crop_field_location.crop_field_zones.some(
            (crop_field_zone) =>
              (crop_field_zone.id = crop_field.crop_field_zone_id)
          )
        );
        this.crop_field_location_id = location?.id;
        this.altitude = crop_field.altitude;
        this.crop_field_type_id = crop_field.crop_field_type_id;
        this.crop_variety_id = crop_field.crop_variety_id;
        this.crop_field_zone_id = crop_field.crop_field_zone_id;
      }
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => this.loadData())
    );
  }
};
</script>
